import { Component, ViewChild } from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, first, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuPanel } from '@angular/material/menu';

import {
  addMeasureSuccess,
  deleteMeasures, deleteMeasuresSuccess,
  deleteMeasureSuccess,
  loadMeasuresSuccess,
  selectMeasure,
  setMeasuresShow,
  shareMeasures,
  shareOffMeasures,
} from '../../../features/measure/measure.actions';

import {
  addCutSuccess,
  deleteCuts,
  deleteCutSuccess,
  retrieveCutsCoordinatesCSV,
  generateCutsDXF,
  loadCutsSuccess,
  selectCut,
  setCutsShow,
  shareCuts,
  shareOffCuts,
  generateCutsReport, deleteCutsSuccess,
} from '../../../features/cut/cut.actions';

import {
  addTrackerSuccess,
  deleteTrackers,
  deleteTrackerSuccess,
  retrieveMarkersCoordinatesCSV,
  loadTrackersElevationSuccess,
  loadTrackersImageSuccess,
  selectTrackerElevation,
  selectTrackerImage,
  setTrackersShow,
  shareOffTrackers,
  shareTrackers,
  retrieveElevationsCoordinatesCSV, deleteTrackersSuccess,
} from '../../../features/tracker/tracker.actions';

import {
  addDetectionSuccess,
  deleteDetectionSuccess,
  generateDetectionsReport,
  selectDetection,
  setDetectionsShow,
} from '../../../features/detection/detection.actions';

import {
  generateVolumesDXF,
  loadVolumesSuccess,
  selectVolume,
  setVolumesShow,
} from '../../../features/volume/volume.actions';

import {
  selectCurrentCutId,
  selectCutTotal,
} from '../../../features/cut/cut.selectors';

import {
  selectCurrentTrackerElevationId,
  selectCurrentTrackerImageId,
  selectTrackerElevationTotal,
  selectTrackerImageTotal,
} from '../../../features/tracker/tracker.selectors';

import {
  selectCurrentMeasureId,
  selectMeasureTotal,
} from '../../../features/measure/measure.selectors';

import {
  selectCurrentVolumeId,
  selectVolumeTotal,
} from '../../../features/volume/volume.selectors';

import { TrackerType } from '../../../enums/tracker';

import {
  selectCurrentDetectionId,
  selectDetectionTotal,
  selectFilteredDetections,
  selectFilteredDetectionsTotal,
} from '../../../features/detection/detection.selectors';

import {
  selectAdvancedFeaturesAvailable,
  selectCurrentSite,
} from '../../../features/site/site.selectors';

import {
  DeleteConfirmDialog,
} from '../../../shared/dialogs/delete-confirm-dialog/delete-confirm.dialog';

import { Site } from '../../../models/site';

import {
  toggleSort,
} from '../../../features/config/config.actions';

import {
  SORT_FIELD,
  SortableListKey,
  SortConfig,
  SortDirection,
  SortField,
} from '../../../enums/sort';

import {
  selectConfigDisplayCuts,
  selectConfigDisplayDetections,
  selectConfigDisplayElevations,
  selectConfigDisplayMarkers,
  selectConfigDisplayMeasures,
  selectConfigDisplayVolumes,
  selectSortConfig,
} from '../../../features/config/config.selectors';

import { InspectionModule } from '../../../enums/inspection-module';
import { selectUser } from '../../../features/user/user.selectors';
import { DetectionStatus } from '../../../enums/detection';
import {
  selectCurrentReferenceBatchId
} from '../../../features/batch/batch.selectors';

type ItemType = 'measure' | 'cut' | 'elevation' | 'marker' | 'detection' | 'volume';
type CategoryKey = ItemType;

interface TreeCategoryNode {
  kind: 'category';
  key: CategoryKey;
  name: string;
  children: TreeItemNode[];
}

interface TreeItemNode {
  kind: 'item';
  type: ItemType;
  id: string;
  children?: never;
}

type TreeNode = TreeCategoryNode | TreeItemNode;

interface FlatCategoryNode {
  kind: 'category';
  level: number;
  expandable: true;
  name: string;
  key: CategoryKey;
}

interface FlatItemNode {
  kind: 'item';
  level: number;
  expandable: false;
  type: ItemType;
  id: string;
}

type FlatNode = FlatCategoryNode | FlatItemNode;
type ControlKey = string;

type CategoryState = {
  selectedCount: number;
  total: number;
  allSelected: boolean;
  indeterminate: boolean;
  hasSelected: boolean;
};

@Component({
    selector: 'items-list',
    templateUrl: './items-list.component.html',
    styleUrls: ['./items-list.component.scss'],
    standalone: false
})
export class ItemsListComponent {
  @ViewChild('measureMoreMenu') measureMoreMenu!: MatMenuPanel<any>;
  @ViewChild('cutMoreMenu') cutMoreMenu!: MatMenuPanel<any>;
  @ViewChild('elevationMoreMenu') elevationMoreMenu!: MatMenuPanel<any>;
  @ViewChild('markerMoreMenu') markerMoreMenu!: MatMenuPanel<any>;
  @ViewChild('volumeMoreMenu') volumeMoreMenu!: MatMenuPanel<any>;
  @ViewChild('detectionMoreMenu') detectionMoreMenu!: MatMenuPanel<any>;

  @ViewChild('measureSortMenu') measureSortMenu!: MatMenuPanel<any>;
  @ViewChild('cutSortMenu') cutSortMenu!: MatMenuPanel<any>;
  @ViewChild('elevationSortMenu') elevationSortMenu!: MatMenuPanel<any>;
  @ViewChild('markerSortMenu') markerSortMenu!: MatMenuPanel<any>;
  @ViewChild('volumeSortMenu') volumeSortMenu!: MatMenuPanel<any>;
  @ViewChild('detectionSortMenu') detectionSortMenu!: MatMenuPanel<any>;

  readonly currentSite$ = this.store.select(selectCurrentSite);
  readonly advancedFeaturesAvailable$ = this.store.select(selectAdvancedFeaturesAvailable);

  readonly cutIdSelected$ = this.store.select(selectCurrentCutId);
  readonly detectionIdSelected$ = this.store.select(selectCurrentDetectionId);
  readonly elevationIdSelected$ = this.store.select(selectCurrentTrackerElevationId);
  readonly markerIdSelected$ = this.store.select(selectCurrentTrackerImageId);
  readonly measureIdSelected$ = this.store.select(selectCurrentMeasureId);
  readonly volumeIdSelected$ = this.store.select(selectCurrentVolumeId);

  readonly measureSortConfig$ = this.store.select(selectSortConfig(SortableListKey.MEASURES));
  readonly cutSortConfig$ = this.store.select(selectSortConfig(SortableListKey.CUTS));
  readonly elevationSortConfig$ = this.store.select(selectSortConfig(SortableListKey.ELEVATIONS));
  readonly markerSortConfig$ = this.store.select(selectSortConfig(SortableListKey.MARKERS));
  readonly volumeSortConfig$ = this.store.select(selectSortConfig(SortableListKey.VOLUMES));
  readonly detectionSortConfig$ = this.store.select(selectSortConfig(SortableListKey.DETECTIONS));

  readonly hasAnyAnnotation$ = combineLatest([
    this.store.select(selectDetectionTotal),
    this.store.select(selectVolumeTotal),
    this.store.select(selectMeasureTotal),
    this.store.select(selectCutTotal),
    this.store.select(selectTrackerElevationTotal),
    this.store.select(selectTrackerImageTotal),
  ]).pipe(
    map((counts) => counts.some((count) => count > 0)),
  );

  readonly inspectionModule = InspectionModule;

  readonly isCategory = (_: number, node: FlatNode) => node.kind === 'category';
  readonly isItem = (_: number, node: FlatNode) => node.kind === 'item';

  readonly SortableListKey = SortableListKey;
  readonly SortField = SORT_FIELD;

  private readonly rawItemsByCategory: Record<CategoryKey, any[]> = {
    measure: [],
    cut: [],
    elevation: [],
    marker: [],
    detection: [],
    volume: [],
  };

  private readonly sortableListKeyByCategory: Record<CategoryKey, SortableListKey> = {
    measure: SortableListKey.MEASURES,
    cut: SortableListKey.CUTS,
    elevation: SortableListKey.ELEVATIONS,
    marker: SortableListKey.MARKERS,
    detection: SortableListKey.DETECTIONS,
    volume: SortableListKey.VOLUMES,
  };

  private readonly nodeKey = (n: FlatNode): ControlKey =>
    n.kind === 'category'
      ? `cat:${n.key}`
      : `item:${n.type}:${n.id}`;

  readonly domTrackBy = (index: number, n?: FlatNode) => {
    if (!n) return `__undef__:${index}`;
    return this.nodeKey(n);
  };

  private readonly transformer = (node: TreeNode, level: number): FlatNode => {
    if (node.kind === 'category') {
      return {
        kind: 'category',
        level,
        expandable: true,
        name: node.name,
        key: node.key,
      };
    }

    return {
      kind: 'item',
      level,
      expandable: false,
      type: node.type,
      id: node.id,
    };
  };

  readonly treeControl = new FlatTreeControl<FlatNode, ControlKey>(
    (n) => n.level,
    (n) => n.expandable,
    { trackBy: (n: FlatNode) => this.nodeKey(n) },
  );

  readonly treeFlattener = new MatTreeFlattener<TreeNode, FlatNode, ControlKey>(
    this.transformer,
    (n) => n.level,
    (n) => n.expandable,
    (n: TreeNode) => (n.kind === 'category' ? n.children : []),
  );

  readonly dataSource = new MatTreeFlatDataSource<TreeNode, FlatNode, ControlKey>(
    this.treeControl,
    this.treeFlattener,
  );

  private readonly selectedByCategory: Record<CategoryKey, Set<string>> = {
    measure: new Set(),
    cut: new Set(),
    elevation: new Set(),
    marker: new Set(),
    detection: new Set(),
    volume: new Set(),
  };

  constructor(
    private readonly store: Store,
    private dialog: MatDialog,
    private readonly actions: Actions,
    private readonly translocoService: TranslocoService,
  ) {
    this.bindLoadStreams();
    this.bindCreateStreams();
    this.bindRemoveStreams();
    this.bindSortStreams();
  }

  private bindSortStreams(): void {
    this.measureSortConfig$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.refreshCategory('measure', 'measures'));

    this.cutSortConfig$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.refreshCategory('cut', 'cuts'));

    this.elevationSortConfig$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.refreshCategory('elevation', 'elevations'));

    this.markerSortConfig$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.refreshCategory('marker', 'markers'));

    this.volumeSortConfig$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.refreshCategory('volume', 'volumes'));

    this.detectionSortConfig$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.refreshCategory('detection', 'detections'));
  }

  private bindLoadStreams(): void {
    this.actions.pipe(ofType(loadMeasuresSuccess), takeUntilDestroyed())
      .subscribe((result: any) => {
        this.rawItemsByCategory.measure = result?.measures ?? [];
        this.refreshCategory('measure', 'measures');
      });

    this.actions.pipe(ofType(loadCutsSuccess), takeUntilDestroyed())
      .subscribe((result: any) => {
        this.rawItemsByCategory.cut = result?.cuts ?? [];
        this.refreshCategory('cut', 'cuts');
      });

    this.actions.pipe(ofType(loadTrackersElevationSuccess), takeUntilDestroyed())
      .subscribe((result: any) => {
        this.rawItemsByCategory.elevation = result?.trackers ?? [];
        this.refreshCategory('elevation', 'elevations');
      });

    this.actions.pipe(ofType(loadTrackersImageSuccess), takeUntilDestroyed())
      .subscribe((result: any) => {
        this.rawItemsByCategory.marker = result?.trackers ?? [];
        this.refreshCategory('marker', 'markers');
      });

    this.store.select(selectFilteredDetections)
      .pipe(takeUntilDestroyed())
      .subscribe((detections) => {
        this.rawItemsByCategory.detection = detections ?? [];
        this.refreshCategory('detection', 'detections');
      });

    this.actions.pipe(ofType(loadVolumesSuccess), takeUntilDestroyed())
      .subscribe((result: any) => {
        this.rawItemsByCategory.volume = result?.volumes ?? [];
        this.refreshCategory('volume', 'volumes');
      });
  }

  private bindCreateStreams(): void {
    this.actions.pipe(ofType(addMeasureSuccess), takeUntilDestroyed())
      .subscribe((result: any) => {
        if (result) {
          this.addItem('measure', 'measures', {
            kind: 'item',
            type: 'measure',
            id: String(result.measure.id),
          });
        }
      });

    this.actions.pipe(ofType(addCutSuccess), takeUntilDestroyed())
      .subscribe((result: any) => {
        if (result) {
          this.addItem('cut', 'cuts', {
            kind: 'item',
            type: 'cut',
            id: String(result.cut.id),
          });
        }
      });

    this.actions.pipe(ofType(addTrackerSuccess), takeUntilDestroyed())
      .subscribe((result: any) => {
        if (result) {
          const key = result.tracker.trackerType === TrackerType.IMAGE ? 'marker' : 'elevation';
          this.addItem(key, `${key}s`, {
            kind: 'item',
            type: key,
            id: String(result.tracker.id),
          });
        }
      });

    this.actions.pipe(ofType(addDetectionSuccess), takeUntilDestroyed())
      .subscribe((result: any) => {
        if (result) {
          this.addItem('detection', 'detections', {
            kind: 'item',
            type: 'detection',
            id: String(result.detection.id),
          });
        }
      });
  }

  private bindRemoveStreams(): void {
    this.actions.pipe(ofType(deleteMeasureSuccess), takeUntilDestroyed())
      .subscribe((result: any) => this.removeItem('measure', result.id));
    this.actions.pipe(ofType(deleteMeasuresSuccess), takeUntilDestroyed())
      .subscribe((result: any) => (result?.ids ?? []).forEach((id: string) => this.removeItem('measure', id)));

    this.actions.pipe(ofType(deleteCutSuccess), takeUntilDestroyed())
      .subscribe((result: any) => this.removeItem('cut', result.id));
    this.actions.pipe(ofType(deleteCutsSuccess), takeUntilDestroyed())
      .subscribe((result: any) => (result?.ids ?? []).forEach((id: string) => this.removeItem('cut', id)));

    this.actions.pipe(ofType(deleteTrackerSuccess), takeUntilDestroyed())
      .subscribe((result: any) => {
        this.removeItem('marker', result.id);
        this.removeItem('elevation', result.id);
      });
    this.actions.pipe(ofType(deleteTrackersSuccess), takeUntilDestroyed())
      .subscribe((result: any) => (result?.ids ?? []).forEach((id: string) => {
        this.removeItem('marker', id);
        this.removeItem('elevation', id);
      }));

    this.actions.pipe(ofType(deleteDetectionSuccess), takeUntilDestroyed())
      .subscribe((result: any) => this.removeItem('detection', result.id));
  }

  private refreshCategory(key: CategoryKey, labelKey: string): void {
    const items = this.rawItemsByCategory[key] ?? [];
    const listKey = this.sortableListKeyByCategory[key];

    this.store.select(selectSortConfig(listKey))
      .pipe(first())
      .subscribe((sortConfig) => {
        this.upsertCategory(
          key,
          labelKey,
          this.toSortedItemNodes(key, items, sortConfig),
        );
      });
  }

  private sortItems(items: any[], sort: SortConfig): any[] {
    if (!sort.field || sort.direction === SortDirection.NONE) {
      return [...items];
    }

    return [...items].sort((a, b) => {
      let result = 0;

      switch (sort.field) {
        case SORT_FIELD.NAME: {
          result = String(a.name ?? '').localeCompare(String(b.name ?? ''));
          break;
        }

        case SORT_FIELD.CONFIDENCE: {
          result = Number(a.confidence ?? 0) - Number(b.confidence ?? 0);
          break;
        }

        case SORT_FIELD.VALUE: {
          result = Number(a.value ?? 0) - Number(b.value ?? 0);
          break;
        }

        case SORT_FIELD.VOLUME: {
          result = Number(a.volume ?? 0) - Number(b.volume ?? 0);
          break;
        }

        case SORT_FIELD.EMERGENCY: {
          const aa = (a.emergency) ? a.emergency.priority : 11;
          const bb = (b.emergency) ? b.emergency.priority : 11;
          result = Number(aa) - Number(bb);
          break;
        }

        default:
          result = 0;
      }

      return sort.direction === SortDirection.ASC ? result : -result;
    });
  }

  private toSortedItemNodes(type: ItemType, items: any[], sortConfig: SortConfig): TreeItemNode[] {
    return this.sortItems(items ?? [], sortConfig).map((item: any) => ({
      kind: 'item',
      type,
      id: String(item.id),
    }));
  }

  private sortCategories(data: TreeNode[]): TreeNode[] {
    const collator = new Intl.Collator(
      this.translocoService.getActiveLang(),
      { sensitivity: 'base' }
    );

    return [...data].sort((a, b) => {
      if (a.kind !== 'category' || b.kind !== 'category') return 0;

      const byName = collator.compare(a.name, b.name);
      if (byName !== 0) return byName;

      // fallback stable si deux labels sont identiques
      return a.key.localeCompare(b.key);
    });
  }

  private snapshotExpandedCategoryKeys(): Set<CategoryKey> {
    const expanded = new Set<CategoryKey>();
    const selectedKeys = this.treeControl.expansionModel.selected as ControlKey[];

    for (const key of selectedKeys) {
      if (typeof key === 'string' && key.startsWith('cat:')) {
        expanded.add(key.replace('cat:', '') as CategoryKey);
      }
    }

    return expanded;
  }

  private restoreExpandedCategoryKeys(expanded: Set<CategoryKey>): void {
    const nodes = (this.treeControl.dataNodes ?? []) as FlatNode[];

    for (const n of nodes) {
      if (n.kind === 'category' && expanded.has(n.key)) {
        this.treeControl.expand(n);
      }
    }
  }

  private getCategoryItemIds(key: CategoryKey): string[] {
    const cat = (this.dataSource.data as TreeNode[])
      .find((n) => n.kind === 'category' && n.key === key) as TreeCategoryNode | undefined;

    return (cat?.children ?? []).map((c) => c.id);
  }

  private pruneSelection(key: CategoryKey): void {
    const valid = new Set(this.getCategoryItemIds(key));
    const set = this.selectedByCategory[key];

    for (const id of [...set]) {
      if (!valid.has(id)) {
        set.delete(id);
      }
    }
  }

  isItemChecked(type: CategoryKey, id: string): boolean {
    return this.selectedByCategory[type].has(id);
  }

  toggleItem(type: CategoryKey, id: string, checked: boolean): void {
    const set = this.selectedByCategory[type];
    if (checked) set.add(id);
    else set.delete(id);
  }

  toggleAllInCategory(key: CategoryKey, checked: boolean): void {
    const ids = this.getCategoryItemIds(key);
    const set = this.selectedByCategory[key];

    if (checked) ids.forEach((id) => set.add(id));
    else set.clear();
  }

  getCategoryState(key: CategoryKey): CategoryState {
    this.pruneSelection(key);

    const total = this.getCategoryItemIds(key).length;
    const selectedCount = this.selectedByCategory[key].size;

    const allSelected = total > 0 && selectedCount === total;
    const hasSelected = selectedCount > 0;
    const indeterminate = hasSelected && !allSelected;

    return { selectedCount, total, allSelected, indeterminate, hasSelected };
  }

  getSelectedIdsForCategory(key: CategoryKey): string[] {
    this.pruneSelection(key);
    return [...this.selectedByCategory[key]];
  }

  private upsertCategory(key: CategoryKey, labelKey: string, children: TreeItemNode[]): void {
    const expanded = this.snapshotExpandedCategoryKeys();
    const label = this.translocoService.translate(labelKey);
    const data = (this.dataSource.data ?? []) as TreeNode[];

    if (!children || children.length === 0) {
      const next = data.filter((n) => !(n.kind === 'category' && n.key === key));
      this.dataSource.data =
        next.length !== data.length
          ? this.sortCategories(next)
          : this.sortCategories([...data]);
      this.restoreExpandedCategoryKeys(expanded);
      this.pruneSelection(key);
      return;
    }

    const idx = data.findIndex((n) => n.kind === 'category' && n.key === key);

    if (idx === -1) {
      this.dataSource.data = this.sortCategories([
        ...data,
        { kind: 'category', key, name: label, children },
      ]);
      this.restoreExpandedCategoryKeys(expanded);
      this.pruneSelection(key);
      return;
    }

    const cat = data[idx] as TreeCategoryNode;
    cat.name = label;
    cat.children = children;

    this.dataSource.data = this.sortCategories([...data]);
    this.restoreExpandedCategoryKeys(expanded);
    this.pruneSelection(key);
  }

  private addItem(key: CategoryKey, labelKey: string, item: TreeItemNode): void {
    const expanded = this.snapshotExpandedCategoryKeys();
    const data = (this.dataSource.data ?? []) as TreeNode[];
    const label = this.translocoService.translate(labelKey);

    let category = data.find((n) => n.kind === 'category' && n.key === key) as TreeCategoryNode | undefined;

    if (!category) {
      category = { kind: 'category', key, name: label, children: [] };
      data.push(category);
    }

    if (!category.children.some((c) => c.id === item.id)) {
      category.children.push(item);
    }

    this.dataSource.data = this.sortCategories([...data]);
    this.restoreExpandedCategoryKeys(expanded);
    this.pruneSelection(key);
  }

  private removeItem(key: CategoryKey, id: string): void {
    const expanded = this.snapshotExpandedCategoryKeys();
    const data = (this.dataSource.data ?? []) as TreeNode[];

    const category = data.find((n) => n.kind === 'category' && n.key === key) as TreeCategoryNode | undefined;
    if (!category) return;

    const idx = category.children.findIndex((c) => c.id === id);
    if (idx === -1) return;

    category.children.splice(idx, 1);
    this.selectedByCategory[key].delete(id);

    if (category.children.length === 0) {
      this.dataSource.data = this.sortCategories(data.filter((n) => n !== category));
    } else {
      this.dataSource.data = this.sortCategories([...data]);
    }

    this.restoreExpandedCategoryKeys(expanded);
    this.pruneSelection(key);
  }

  onNodeClick(node: FlatNode): void {
    if (node.kind !== 'item') return;

    if (node.type === 'measure') this.store.dispatch(selectMeasure({ id: node.id }));
    else if (node.type === 'cut') this.store.dispatch(selectCut({ id: node.id }));
    else if (node.type === 'elevation') this.store.dispatch(selectTrackerElevation({ id: node.id }));
    else if (node.type === 'marker') this.store.dispatch(selectTrackerImage({ id: node.id }));
    else if (node.type === 'detection') this.store.dispatch(selectDetection({ id: node.id }));
    else if (node.type === 'volume') this.store.dispatch(selectVolume({ id: node.id }));
  }

  onDeleteCategory(key: CategoryKey): void {
    const ids = this.getSelectedIdsForCategory(key);
    if (ids.length === 0) return;

    const deleteDialogRef = this.dialog.open(DeleteConfirmDialog, {
      disableClose: false,
    });

    deleteDialogRef.componentInstance.message = this.translocoService.translate(
      'delete_selected_items',
      { count: ids.length },
    );

    deleteDialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.currentSite$
        .pipe(first())
        .subscribe((site: Site | null | undefined) => {
          if (!site) return;

          if (key === 'measure') {
            this.store.dispatch(deleteMeasures({
              organizationId: site.organization,
              siteId: site.id,
              batchId: site.lastBatch,
              ids,
            }));
          }

          if (key === 'cut') {
            this.store.dispatch(deleteCuts({
              organizationId: site.organization,
              siteId: site.id,
              ids,
            }));
          }

          if (key === 'elevation' || key === 'marker') {
            this.store.dispatch(deleteTrackers({
              organizationId: site.organization,
              siteId: site.id,
              ids,
            }));
          }
        });
    });
  }

  onShowCategory(key: CategoryKey): void {
    const ids = this.getSelectedIdsForCategory(key);
    if (ids.length === 0) return;

    if (key === 'measure') this.store.dispatch(setMeasuresShow({ ids, show: true }));
    if (key === 'cut') this.store.dispatch(setCutsShow({ ids, show: true }));
    if (key === 'elevation' || key === 'marker') this.store.dispatch(setTrackersShow({ ids, show: true }));
    if (key === 'detection') this.store.dispatch(setDetectionsShow({ ids, show: true }));
    if (key === 'volume') this.store.dispatch(setVolumesShow({ ids, show: true }));
  }

  onHideCategory(key: CategoryKey): void {
    const ids = this.getSelectedIdsForCategory(key);
    if (ids.length === 0) return;

    if (key === 'measure') this.store.dispatch(setMeasuresShow({ ids, show: false }));
    if (key === 'cut') this.store.dispatch(setCutsShow({ ids, show: false }));
    if (key === 'elevation' || key === 'marker') this.store.dispatch(setTrackersShow({ ids, show: false }));
    if (key === 'detection') this.store.dispatch(setDetectionsShow({ ids, show: false }));
    if (key === 'volume') this.store.dispatch(setVolumesShow({ ids, show: false }));
  }

  onShareCategory(key: CategoryKey): void {
    const ids = this.getSelectedIdsForCategory(key);
    if (ids.length === 0) return;

    this.currentSite$
      .pipe(first())
      .subscribe((site: Site | null | undefined) => {
        if (!site) return;

        if (key === 'measure') {
          this.store.dispatch(shareMeasures({
            organizationId: site.organization,
            siteId: site.id,
            batchId: site.lastBatch,
            ids,
          }));
        }

        if (key === 'cut') {
          this.store.dispatch(shareCuts({
            organizationId: site.organization,
            siteId: site.id,
            ids,
          }));
        }

        if (key === 'elevation' || key === 'marker') {
          this.store.dispatch(shareTrackers({
            organizationId: site.organization,
            siteId: site.id,
            ids,
          }));
        }
      });
  }

  onShareOffCategory(key: CategoryKey): void {
    const ids = this.getSelectedIdsForCategory(key);
    if (ids.length === 0) return;

    this.currentSite$
      .pipe(first())
      .subscribe((site: Site | null | undefined) => {
        if (!site) return;

        if (key === 'measure') {
          this.store.dispatch(shareOffMeasures({
            organizationId: site.organization,
            siteId: site.id,
            batchId: site.lastBatch,
            ids,
          }));
        }

        if (key === 'cut') {
          this.store.dispatch(shareOffCuts({
            organizationId: site.organization,
            siteId: site.id,
            ids,
          }));
        }

        if (key === 'elevation' || key === 'marker') {
          this.store.dispatch(shareOffTrackers({
            organizationId: site.organization,
            siteId: site.id,
            ids,
          }));
        }
      });
  }

  getMoreMenu(nodeKey: string): MatMenuPanel<any> | null {
    switch (nodeKey) {
      case 'measure': return this.measureMoreMenu;
      case 'cut': return this.cutMoreMenu;
      case 'elevation': return this.elevationMoreMenu;
      case 'marker': return this.markerMoreMenu;
      case 'volume': return this.volumeMoreMenu;
      case 'detection': return this.detectionMoreMenu;
      default: return null;
    }
  }

  getSortMenu(nodeKey: string): MatMenuPanel<any> | null {
    switch (nodeKey) {
      case 'measure': return this.measureSortMenu;
      case 'cut': return this.cutSortMenu;
      case 'elevation': return this.elevationSortMenu;
      case 'marker': return this.markerSortMenu;
      case 'volume': return this.volumeSortMenu;
      case 'detection': return this.detectionSortMenu;
      default: return null;
    }
  }

  onClickGenerateDetectionsReport(): void {
    this.store.select(selectCurrentSite)
      .pipe(first())
      .subscribe((site: Site | undefined | null) => {
        if (!site) return;

        const ids = this.getSelectedIdsForCategory('detection');
        if (ids.length === 0) return;

        this.store.dispatch(generateDetectionsReport({
          organizationId: site.organization,
          siteId: site.id,
          formats: ['docx'],
          ids,
        }));
      });
  }

  onClickGenerateVolumesDXF(): void {
    this.store.select(selectCurrentSite)
      .pipe(first())
      .subscribe((site) => {
        if (!site) return;
        const ids = this.getSelectedIdsForCategory('volume');
        if (ids.length === 0) return;

        this.store.dispatch(generateVolumesDXF({
          organizationId: site.organization,
          siteId: site.id,
          ids,
          batchId: site.lastBatch,
        }));
      });
  }

  onClickGenerateCutsDXF(): void {
    combineLatest([
      this.store.select(selectCurrentSite),
      this.store.select(selectCurrentReferenceBatchId),
    ]).pipe(first())
      .subscribe(([site, referenceBatchId]) => {
        if (!site) return;
        const ids = this.getSelectedIdsForCategory('cut');
        if (ids.length === 0) return;

        this.store.dispatch(generateCutsDXF({
          organizationId: site.organization,
          siteId: site.id,
          ids,
          batchId: site.lastBatch,
          referenceBatchId
        }));
      });
  }

  onClickGenerateCutsReport(): void {
    combineLatest([
      this.store.select(selectCurrentSite),
      this.store.select(selectCurrentReferenceBatchId),
    ]).pipe(first())
      .subscribe(([site, referenceBatchId]) => {
        if (!site) return;
        const ids = this.getSelectedIdsForCategory('cut');
        if (ids.length === 0) return;

        this.store.dispatch(generateCutsReport({
          organizationId: site.organization,
          siteId: site.id,
          formats: ['docx'],
          ids,
          batchId: site.lastBatch,
          referenceBatchId
        }));
      });
  }

  onClickRetrieveCutsCoordinatesCSV(): void {
    this.store.select(selectCurrentSite)
      .pipe(first())
      .subscribe((site: Site | undefined | null) => {
        if (!site) return;

        const ids = this.getSelectedIdsForCategory('cut');
        if (ids.length === 0) return;

        this.store.dispatch(retrieveCutsCoordinatesCSV({
          organizationId: site.organization,
          siteId: site.id,
          ids,
        }));
      });
  }

  onClickRetrieveElevationsCoordinantesCSV(): void {
    this.store.select(selectCurrentSite)
      .pipe(first())
      .subscribe((site: Site | undefined | null) => {
        if (!site) return;

        const ids = this.getSelectedIdsForCategory('elevation');
        if (ids.length === 0) return;

        this.store.dispatch(retrieveElevationsCoordinatesCSV({
          organizationId: site.organization,
          siteId: site.id,
          ids,
        }));
      });
  }

  onClickRetrieveMarkersCoordinantesCSV(): void {
    this.store.select(selectCurrentSite)
      .pipe(first())
      .subscribe((site: Site | undefined | null) => {
        if (!site) return;

        const ids = this.getSelectedIdsForCategory('marker');
        if (ids.length === 0) return;

        this.store.dispatch(retrieveMarkersCoordinatesCSV({
          organizationId: site.organization,
          siteId: site.id,
          ids,
        }));
      });
  }

  onClickSort(event: MouseEvent, list: SortableListKey, field: SortField): void {
    event.stopPropagation();
    this.store.dispatch(toggleSort({ list, field }));
  }

  getSortIcon(sort: SortConfig, field: SortField): string {
    if (sort.field !== field) {
      return 'unfold_more';
    }

    switch (sort.direction) {
      case SortDirection.ASC:
        return 'arrow_upward';
      case SortDirection.DESC:
        return 'arrow_downward';
      default:
        return 'unfold_more';
    }
  }

  getConfigDisplayForNode$(nodeKey: string): Observable<boolean> {
    switch (nodeKey) {
      case 'measure': return this.store.select(selectConfigDisplayMeasures);
      case 'cut': return this.store.select(selectConfigDisplayCuts);
      case 'elevation': return this.store.select(selectConfigDisplayElevations);
      case 'marker': return this.store.select(selectConfigDisplayMarkers);
      case 'volume': return this.store.select(selectConfigDisplayVolumes);
      case 'detection': return this.store.select(selectConfigDisplayDetections);
      default: return of(false);
    }
  }

  getCateroryItemCount$(nodeKey: string): Observable<number> {
    switch (nodeKey) {
      case 'measure': return this.store.select(selectMeasureTotal);
      case 'cut': return this.store.select(selectCutTotal);
      case 'elevation': return this.store.select(selectTrackerElevationTotal);
      case 'marker': return this.store.select(selectTrackerImageTotal);
      case 'volume': return this.store.select(selectVolumeTotal);
      case 'detection': return this.store.select(selectFilteredDetectionsTotal);
      default: return of(0);
    }
  }
}
